import { ChangeDetectorRef, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { AdministrationRole, AdministrationService, AdministrationUser, EditorStoreOption } from '../../../services/administration/administration.service';
import { applicationRoles, hasRole } from '../../../guards/auth/roles';
import { GlobalDataService } from '../../../services/global-data/global-data.service';

@Component({
  selector: 'app-role-dialog', standalone: true,
  imports: [FormsModule, MatDialogModule, MatCheckboxModule, MatButtonModule, MatFormFieldModule, MatSelectModule],
  template: `
    <h2 mat-dialog-title>Rediger roller</h2>
    <mat-dialog-content>
      <p>{{ data.displayName || data.userId }}</p>
      <p class="identity">{{ data.userId }}</p>
      @if (!canEdit) {
        <p role="alert">Du har ikke længere adgang til at ændre rolletildelinger.</p>
      }
      @for (role of applicationRoles; track role) {
        <mat-checkbox [ngModel]="roles.has(role)" (ngModelChange)="toggleRole(role, $event)"
          [disabled]="saving || !canEdit || (role === 'Administrator' && !canAssignAdministrator)">{{ role }}</mat-checkbox>
      }
      @if (storeLoadFailed) {
        <p role="alert">Butikkerne kunne ikke hentes.</p>
        <button mat-button (click)="loadStores()">Prøv igen</button>
      }
      <mat-form-field class="w-100">
        <mat-label>SendoutEditor · Butikker</mat-label>
        <mat-select multiple [(ngModel)]="editorStoreIds" [disabled]="saving || !canEdit || !storesLoaded">
          @for (store of stores; track store.id) {
            <mat-option [value]="store.id">{{ store.place }} ({{ store.id }}){{ store.isActive ? '' : ' · Inaktiv' }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (otherAssignments.length) {
        <section class="other-roles" aria-label="Andre rolletildelinger">
          <p>Andre rolletildelinger (ændres ikke):</p>
          <ul>@for (assignment of otherAssignments; track $index) { <li>{{ assignment.role }} — {{ assignment.scopeType }}: {{ assignment.scope ?? '—' }}</li> }</ul>
        </section>
      }
      @if (error) { <p class="error" role="alert">{{ error }}</p> }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="cancel()" [disabled]="saving">Annuller</button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="saving || !canEdit || !dirty">{{ saving ? 'Gemmer…' : 'Gem' }}</button>
    </mat-dialog-actions>
  `,
  styles: [`.identity,.other-roles{color:#666}.error{color:#b3261e} mat-checkbox{display:block;margin:8px 0}`]
})
export class RoleDialogComponent {
  readonly applicationRoles = applicationRoles;
  readonly roles: Set<string>;
  editorStoreIds: number[];
  stores: EditorStoreOption[] = [];
  storesLoaded = false;
  storeLoadFailed = false;
  saving = false;
  error = '';
  private readonly destroyRef = inject(DestroyRef);
  readonly otherAssignments: NonNullable<AdministrationUser['otherAssignments']>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AdministrationUser,
    private ref: MatDialogRef<RoleDialogComponent>,
    private service: AdministrationService,
    private cdr: ChangeDetectorRef,
    private global: GlobalDataService
  ) {
    this.roles = new Set(data.roles);
    this.editorStoreIds = [...data.editorStoreIds];
    this.loadStores();
    this.otherAssignments = data.otherAssignments ?? [];
    this.global.getSessionData().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
    ref.disableClose = true;
    ref.backdropClick().subscribe(() => this.cancel());
    ref.keydownEvents().subscribe(event => { if (event.key === 'Escape') this.cancel(); });
  }

  get canAssignAdministrator(): boolean {
    return hasRole(this.global.getSessionData().value, 'Administrator');
  }

  get canEdit(): boolean {
    return hasRole(this.global.getSessionData().value, 'UserManager');
  }

  loadStores(): void {
    this.storeLoadFailed = false;
    this.service.getEditorStores().subscribe({
      next: stores => {
        this.stores = stores.filter(store => store.id > 0);
        this.storesLoaded = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.storeLoadFailed = true;
        this.cdr.markForCheck();
      },
    });
  }

  toggleRole(role: AdministrationRole, assigned: boolean): void {
    if (!this.canEdit || role === 'Administrator' && !this.canAssignAdministrator) return;
    if (assigned) this.roles.add(role);
    else this.roles.delete(role);
  }

  get dirty(): boolean {
    return this.rolesChanged || this.editorStoresChanged;
  }

  private get rolesChanged(): boolean {
    return this.roles.size !== this.data.roles.length ||
      this.data.roles.some(role => !this.roles.has(role));
  }

  private get editorStoresChanged(): boolean {
    return this.editorStoreIds.length !== this.data.editorStoreIds.length ||
      this.data.editorStoreIds.some(id => !this.editorStoreIds.includes(id));
  }

  canClose(): boolean {
    return !this.saving && (!this.dirty || window.confirm('Forkast ændringerne?'));
  }
  cancel(): void {
    if (this.canClose()) this.ref.close();
  }
  save(): void {
    if (this.saving || !this.canEdit || !this.dirty) return;
    const roles = applicationRoles.filter(role =>
      role === 'Administrator' && !this.canAssignAdministrator
        ? this.data.roles.includes(role)
        : this.roles.has(role)
    );
    this.saving = true; this.error = '';
    this.cdr.markForCheck();
    this.service.updateRoles(this.data.id, {
      ...(this.rolesChanged ? { roles, expectedRoles: this.data.roles } : {}),
      ...(this.editorStoresChanged ? { editorStoreIds: this.editorStoreIds, expectedEditorStoreIds: this.data.editorStoreIds } : {}),
    }).subscribe({
      next: user => { this.saving = false; this.cdr.markForCheck(); this.ref.close(user); },
      error: err => {
        this.saving = false;
        this.error = err.status === 403
          ? 'Du har ikke adgang til at gemme disse rolletildelinger.'
          : err.status === 409
          ? err.error?.message || 'Den sidste administrator kan ikke fjernes.'
          : 'Rollerne kunne ikke gemmes. Prøv igen.';
        this.cdr.markForCheck();
      }
    });
  }
}
